import type { ThunkDispatch } from 'redux-thunk';
import type { AnyAction } from 'redux';
import AuthActions from 'app/shared/flux/actions/AuthActions';
import constants from 'app/shared/constants/ConstantsBundle';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import UserActions from 'app/shared/flux/actions/UserActions';
import userUtils from 'app/shared/utils/userUtils';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { logError } from '@zg-rentals/log-error';
import type { RootReduxState } from 'app/shared/flux/reducers';
import type { ThunkResult } from 'app/types/redux.type';

const logger = getGlobalLogger('actions/contact');

type AuthResponse = {
  success?: boolean;
  loggedIn?: boolean;
  status?: string;
};

type InquiryResponse = {
  error?: Error | string;
};

type InquiryParams = {
  email: string;
  phone?: string;
  name?: string;
  textToCache?: string;
  displayName?: string;
  address?: string;
  defaultMessage?: boolean;
};

type SendInquiryParams = {
  listing: Record<string, unknown>;
  inquiryParams: InquiryParams;
  rentalSubmitId?: string;
};

const ContactActions = {
  freepassLogin(email: string): ThunkResult<Promise<AuthResponse | boolean>> {
    return (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>) => {
      return dispatch(AuthActions.login.load({ email, dontSendWelcomeEmail: false }))
        .then((data: AuthResponse) => {
          // allow the inquiry to be sent, but don't log the user in afterwards if they aren't authd
          if (data.success && !data.loggedIn) {
            return true;
          } else if (data.status === 'USER_ALREADY_EXISTS' && !data.loggedIn) {
            return data;
          } else {
            userUtils.successLoginRedirect('');
            return data;
          }
        })
        .catch((err: unknown) => {
          logger?.warn(err, 'CONTACT_FORM_FREEPASS_LOGIN_ERROR');
          return false;
        });
    };
  },
  loginUser(userLoggedIn: boolean, inputEmail: string): ThunkResult<Promise<AuthResponse | boolean>> {
    return (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>) => {
      if (!userLoggedIn) {
        return dispatch(ContactActions.freepassLogin(inputEmail));
      } else {
        return Promise.resolve(true);
      }
    };
  },
  submitContact({ listing, inquiryParams, rentalSubmitId }: SendInquiryParams): ThunkResult<Promise<InquiryResponse>> {
    return (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>) => {
      return dispatch(
        ListingEngineActions.sendInquiry({
          options: inquiryParams,
          listing,
          rentalSubmitId,
        }),
      ).then((response: InquiryResponse) => {
        if (response.error) {
          getGlobalMonitor()?.count({ name: 'ContactForm Submit Error' });
          logError({
            error: response.error,
            errorType: 'ContactActionsError',
            context: 'ListingEngineActions.sendInquiry responded with an error',
          });
        } else {
          getGlobalMonitor()?.count({ name: 'ContactForm Submit Success' });
          const updateMessageParams = {
            autoGeneratedMessage: false,
            email: inquiryParams.email,
            phone: inquiryParams.phone,
            name: inquiryParams.name,
            text: inquiryParams.textToCache || '',
            displayName: inquiryParams.displayName,
            address: inquiryParams.address,
            defaultMessage: inquiryParams.defaultMessage,
          };

          dispatch(UserActions.inquiry.updateMessageCache(updateMessageParams));
        }

        return response;
      });
    };
  },
  // HPWEB-5993: CTA A/B test
  updateCtaButtonContext(source = ''): ThunkResult<AnyAction> {
    return (dispatch: ThunkDispatch<RootReduxState, unknown, AnyAction>) => {
      return dispatch({
        type: constants.UPDATE_CTA_BUTTON_CONTEXT,
        payload: source,
      });
    };
  },
};

export default ContactActions;
